import React, { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PostListSkeleton } from './Skeletons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useMobile } from '../utils/useMobile';
import { getMediaUrl } from '../utils/mediaUrl';
import { notifyIncomingMessage, showWebNotification } from '../utils/notificationAudio';
import { triggerHaptic } from '../utils/nativeBridge';
import { useSwipeBack } from '../utils/useSwipeBack';
import RightSidebar from './RightSidebar';
import HalqaBanner from './HalqaBanner';
import HalqaStartButton from './HalqaStartButton';
import SiteLinks, { SITE_LINKS } from './SiteLinks';
import BazzefQrModal from './BazzefQrModal';

const SIDEBAR_WIDTH = 250;
const COLLAPSED_SIDEBAR_WIDTH = 74;

const AppLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useSocket();
  const isMobile = useMobile();
  const isRtl = i18n.language === 'ar' || (typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('bazzef_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('bazzef_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [communitiesUnreadCount, setCommunitiesUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Mobile "+" menu: choose between a post and a Lamha
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [hideMobileNav, setHideMobileNav] = useState(false);
  const [profileSummary, setProfileSummary] = useState(null);
  const [incomingMessageToast, setIncomingMessageToast] = useState(null);
  const [bannerIsDismissing, setBannerIsDismissing] = useState(false);
  const bannerTouchStartYRef = useRef(null);
  const [bannerDragY, setBannerDragY] = useState(0);
  const bannerDismissTimeoutRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  const dismissIncomingBanner = useCallback(() => {
    triggerHaptic('light');
    setBannerIsDismissing(true);
    if (bannerDismissTimeoutRef.current) clearTimeout(bannerDismissTimeoutRef.current);
    bannerDismissTimeoutRef.current = setTimeout(() => {
      setIncomingMessageToast(null);
      setBannerIsDismissing(false);
      setBannerDragY(0);
    }, 240);
  }, []);

  useEffect(() => {
    return () => {
      if (bannerDismissTimeoutRef.current) {
        clearTimeout(bannerDismissTimeoutRef.current);
      }
    };
  }, []);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const mobileSearchInputRef = useRef(null);
  const isMessagesPage = location.pathname.startsWith('/messages');
  const isChatActive = /^\/messages\/[^/]+/.test(location.pathname);
  const isPostDetail = /^\/(post|p|mobile\/post)\/[^/]+/.test(location.pathname);
  // The share screen is laid out like compose: full screen with its own header
  const isCompose = location.pathname === '/compose' || location.pathname === '/share';
  const isHalqaPage = location.pathname.startsWith('/halqa');
  const isHalqaRoom = /^\/halqa\/[^/]+/.test(location.pathname);
  const isLamha = /^\/lamha(\/|$)/.test(location.pathname);
  const isLamhaStudio = location.pathname === '/lamha/create';
  const shouldHideMobileNav = hideMobileNav || isChatActive || isPostDetail || isCompose || isHalqaRoom || isLamhaStudio;

  // Swipe sideways to go back, on every screen except the one you start on (nowhere to go
  // back to), the composer (it would drop the draft) and Lamha (its feed has its own swipe)
  const homePath = user ? '/home' : '/explore';
  const swipeIndicatorRef = useRef(null);
  useSwipeBack({
    enabled: isMobile && !isChatActive && location.pathname !== homePath && !isCompose && !isLamha && !drawerOpen && !mobileSearchOpen,
    indicatorRef: swipeIndicatorRef,
    onBack: () => {
      if (window.history.state && window.history.state.idx > 0) navigate(-1);
      else navigate(homePath, { replace: true });
    },
  });

  const getHeaderConfig = () => {
    const path = location.pathname;
    if (path === '/home') {
      return { type: 'brand', left: 'avatar', center: 'brand', right: 'search' };
    }
    if (path === '/explore') {
      return { type: 'brand', left: 'avatar', center: 'brand', right: 'search' };
    }
    if (path === '/notifications') {
      return { type: 'standard', left: 'avatar', title: t('nav.notifications', 'Notifications'), right: 'search' };
    }
    if (path.startsWith('/messages')) {
      return { type: 'standard', left: 'avatar', title: t('nav.messages', 'Messages'), right: 'compose' };
    }
    if (path === '/settings') {
      return { type: 'child', left: 'back', title: t('nav.settings', 'Settings'), right: null };
    }
    if (path.startsWith('/settings/')) {
      const section = path.split('/')[2];
      const titles = { account: 'Account', password: 'Password', privacy: 'Privacy and blocking', language: 'Language', notifications: 'Notifications', delete: 'Delete account' };
      return { type: 'child', left: 'back', title: t(`settings.section_${section}`, titles[section] || t('nav.settings', 'Settings')), right: null };
    }
    if (path === '/bookmarks') {
      return { type: 'child', left: 'back', title: t('nav.bookmarks', 'Bookmarks'), right: null };
    }
    if (path === '/communities') {
      // Creating one is the page's main action (its own header with the Create link is desktop-only)
      return { type: 'standard', left: 'avatar', title: t('nav.communities', 'Communities'), right: user ? 'createCommunity' : 'search' };
    }
    if (path === '/communities/create') {
      return { type: 'child', left: 'back', title: t('communities.create', 'Create Community'), right: null };
    }
    if (/^\/communit(ies|y)\/[^/]+/.test(path) || /^\/c\/[^/]+/.test(path)) {
      return { type: 'child', left: 'back', title: t('nav.communities', 'Community'), right: 'search' };
    }
    if (path === '/marketplace') {
      return { type: 'standard', left: 'avatar', title: t('nav.marketplace', 'Marketplace'), right: 'search' };
    }
    if (path === '/impact' || path === '/mouaawanah' || path === '/hirfa' || path === '/tahaqqoq' || path === '/dirham') {
      return { type: 'standard', left: 'avatar', title: t('nav.impact', 'Civic Impact'), right: 'search' };
    }
    if (path === '/marketplace/create') {
      return { type: 'child', left: 'back', title: t('marketplace.createListing', 'Sell an Item'), right: null };
    }
    if (path.startsWith('/marketplace/edit/')) {
      return { type: 'child', left: 'back', title: t('marketplace.editListing', 'Edit Listing'), right: null };
    }
    if (path.startsWith('/marketplace/item/')) {
      return { type: 'child', left: 'back', title: t('nav.marketplace', 'Marketplace'), right: 'search' };
    }
    if (/^\/(profile|u)\/[^/]+/.test(path)) {
      const handle = path.replace(/^\/(profile|u)\//, '').split('/')[0];
      const isFollow = path.includes('/following') || path.includes('/followers');
      return {
        type: 'child',
        left: 'back',
        title: isFollow
          ? (path.includes('/followers') ? t('profile.followers', 'Followers') : t('profile.following', 'Following'))
          : `@${handle}`,
        right: 'search',
      };
    }
    if (/^\/follow\//.test(path)) {
      return {
        type: 'child',
        left: 'back',
        title: path.includes('followers') ? t('profile.followers', 'Followers') : t('profile.following', 'Following'),
        right: null,
      };
    }
    if (/^\/(post|p|mobile\/post)\/[^/]+/.test(path)) {
      return { type: 'child', left: 'back', title: t('nav.post', 'Post'), right: null };
    }
    if (path === '/search') {
      return { type: 'child', left: 'back', title: t('nav.search', 'Search'), right: 'search' };
    }
    if (/^\/(hashtag|tag)\/[^/]+/.test(path)) {
      const tag = path.replace(/^\/(hashtag|tag)\//, '').split('/')[0];
      return { type: 'child', left: 'back', title: `#${decodeURIComponent(tag)}`, right: 'search' };
    }
    if (path === '/halqa') {
      return { type: 'child', left: 'back', title: t('nav.halqa', 'Halqa Spaces'), right: null };
    }
    if (path === '/admin') {
      return { type: 'child', left: 'back', title: 'Admin', right: null };
    }
    const sitePage = SITE_LINKS.find((link) => link.to === path);
    if (sitePage) {
      return { type: 'child', left: 'back', title: t(`site.${sitePage.key}Title`, sitePage.title), right: null };
    }
    return { type: 'brand', left: 'avatar', center: 'brand', right: 'search' };
  };

  // Close mobile search when navigating
  useEffect(() => {
    setMobileSearchOpen(false);
    setMobileSearchQuery('');
  }, [location.pathname]);

  // Focus search input when opened
  useEffect(() => {
    if (mobileSearchOpen) {
      const timer = setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [mobileSearchOpen]);

  const handleMobileSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const trimmed = mobileSearchQuery.trim();
    if (!trimmed) return;
    triggerHaptic('light');
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setMobileSearchOpen(false);
  };

  const fetchUnreadCount = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get('/api/messages/unread-count');
      setUnreadCount(data.count ?? 0);
    } catch {
      setUnreadCount(0);
    }
  };

  const fetchUnreadNotificationsCount = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get('/api/notifications/unread-count');
      setUnreadNotificationsCount(data.count ?? 0);
    } catch {
      setUnreadNotificationsCount(0);
    }
  };

  const fetchCommunitiesUnreadCount = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get('/api/communities/unread-total');
      setCommunitiesUnreadCount(data.totalUnreadBuzzCount ?? 0);
    } catch {
      setCommunitiesUnreadCount(0);
    }
  };

  const fetchProfileSummary = async () => {
    if (!user?.handle) return;
    try {
      const { data } = await axios.get(`/api/users/${encodeURIComponent(user.handle)}`);
      setProfileSummary({
        username: data.username,
        handle: data.handle,
        profilePicture: data.profilePicture,
        followersCount: data.followersCount ?? 0,
        followingCount: data.followingCount ?? 0,
      });
    } catch {
      setProfileSummary({
        username: user.username,
        handle: user.handle,
        profilePicture: user.profilePicture,
        followersCount: 0,
        followingCount: 0,
      });
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchUnreadNotificationsCount();
    fetchCommunitiesUnreadCount();
  }, [user?.id]);

  useEffect(() => {
    fetchProfileSummary();
  }, [user?.id, user?.handle]);

  useEffect(() => {
    if (location.pathname !== '/messages') fetchUnreadCount();
    if (location.pathname !== '/notifications') fetchUnreadNotificationsCount();
    else setUnreadNotificationsCount(0);
    if (!location.pathname.startsWith('/communities')) fetchCommunitiesUnreadCount();
  }, [location.pathname]);

  useEffect(() => {
    if (!socket) return;

    const handleNewMsg = (msg) => {
      fetchUnreadCount();

      if (!msg || !msg.senderId || String(msg.senderId) === String(user?.id)) {
        return;
      }

      const currentChatId = location.pathname.startsWith('/messages/')
        ? location.pathname.replace('/messages/', '').split('/')[0]
        : null;

      const isCurrentActiveChat =
        currentChatId &&
        (String(currentChatId) === String(msg.senderId) ||
          (msg.sender?.handle && currentChatId.toLowerCase() === msg.sender.handle.toLowerCase()));

      const isTabBackgrounded = typeof document !== 'undefined' && document.hidden;

      // 1. Play audio chime and mobile haptic feedback
      if (isTabBackgrounded || !isCurrentActiveChat) {
        notifyIncomingMessage();
      }

      // 2. Browser Desktop Notification (when Web tab is unfocused / hidden)
      if (isTabBackgrounded) {
        showWebNotification(
          msg.sender?.username || msg.sender?.handle || t('messages.title', 'Messages'),
          {
            body: msg.content || (msg.mediaUrl ? '📷 Attachment' : 'New direct message'),
            icon: msg.sender?.profilePicture || '/logo192.png',
            tag: `msg-${msg.senderId}`,
          },
          () => {
            navigate(`/messages/${msg.senderId}`);
          }
        );
      }

      // 3. Foreground In-App Toast Banner (when looking at app but not in that conversation)
      if (!isCurrentActiveChat) {
        setBannerIsDismissing(false);
        setBannerDragY(0);
        setIncomingMessageToast({
          id: msg.id || Date.now(),
          type: 'message',
          senderId: msg.senderId,
          senderName: msg.sender?.username || msg.sender?.handle || 'User',
          senderHandle: msg.sender?.handle || '',
          avatar: msg.sender?.profilePicture,
          isVerified: msg.sender?.is_verified,
          content: msg.content || (msg.mediaUrl ? '📷 Attachment' : 'Sent you a message'),
          targetUrl: `/messages/${msg.senderId}`,
        });
      }
    };

    const handleNewNotif = (notif) => {
      if (location.pathname !== '/notifications') {
        setUnreadNotificationsCount((prev) => prev + 1);
      }

      if (!notif || !notif.actor || String(notif.actor.id) === String(user?.id)) {
        return;
      }

      const isTabBackgrounded = typeof document !== 'undefined' && document.hidden;
      if (isTabBackgrounded || location.pathname !== '/notifications') {
        notifyIncomingMessage();
      }

      let description = '';
      if (notif.type === 'like') {
        description = t('notifications.likedPost', 'liked your post');
      } else if (notif.type === 're-bazz') {
        description = t('notifications.rebazzedPost', 're-bazzed your post');
      } else if (notif.type === 'reply') {
        description = t('notifications.commentedPost', 'commented on your post');
      } else if (notif.type === 'mention') {
        description = t('notifications.mentionedYou', 'mentioned you in a post');
      } else if (notif.type === 'follow') {
        description = t('notifications.followedYou', 'started following you');
      } else {
        description = t('notifications.newAlert', 'interacted with your content');
      }

      const targetUrl = notif.post_id
        ? `/post/${notif.post_id}`
        : notif.actor?.handle
        ? `/profile/${notif.actor.handle}`
        : '/notifications';

      if (isTabBackgrounded) {
        showWebNotification(
          notif.actor.username || notif.actor.handle || 'Bazzef',
          {
            body: description,
            icon: notif.actor.profile_picture || '/logo192.png',
            tag: `notif-${notif.id || Date.now()}`,
          },
          () => {
            navigate(targetUrl);
          }
        );
      }

      if (location.pathname !== '/notifications') {
        setBannerIsDismissing(false);
        setBannerDragY(0);
        setIncomingMessageToast({
          id: notif.id || Date.now(),
          type: notif.type,
          senderName: notif.actor.username || notif.actor.handle || 'Someone',
          senderHandle: notif.actor.handle || '',
          avatar: notif.actor.profile_picture,
          isVerified: notif.actor.is_verified,
          content: description,
          targetUrl,
        });
      }
    };

    socket.on('new-message', handleNewMsg);
    socket.on('new-notification', handleNewNotif);

    return () => {
      socket.off('new-message', handleNewMsg);
      socket.off('new-notification', handleNewNotif);
    };
  }, [socket, location.pathname, user?.id, t, navigate]);

  // Auto-dismiss in-app notification after 5.5s
  useEffect(() => {
    if (!incomingMessageToast || bannerIsDismissing) return;
    const timer = setTimeout(() => {
      dismissIncomingBanner();
    }, 5500);
    return () => clearTimeout(timer);
  }, [incomingMessageToast, bannerIsDismissing, dismissIncomingBanner]);

  // Dismiss if user navigates to the sender's conversation or target
  useEffect(() => {
    if (incomingMessageToast) {
      const isTargetChat =
        (incomingMessageToast.senderId && location.pathname === `/messages/${incomingMessageToast.senderId}`) ||
        (incomingMessageToast.senderHandle &&
          location.pathname.toLowerCase() === `/messages/${incomingMessageToast.senderHandle.toLowerCase()}`) ||
        (incomingMessageToast.targetUrl && location.pathname === incomingMessageToast.targetUrl);
      if (isTargetChat) {
        setIncomingMessageToast(null);
        setBannerIsDismissing(false);
      }
    }
  }, [location.pathname, incomingMessageToast]);

  useEffect(() => {
    setDrawerOpen(false);
    setCreateMenuOpen(false);
    setHideMobileNav(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate('/login');
  };

  const closeDrawer = () => setDrawerOpen(false);

  const navLinkClass = (path) => {
    const match = path === '/home' ? location.pathname === '/home' : location.pathname.startsWith(path);
    if (sidebarCollapsed && !isMobile) {
      return `flex items-center justify-center w-11 h-11 rounded-full mx-auto transition-all text-[20px] relative ${
        match
          ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 font-bold shadow-xs'
          : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/80 hover:text-zinc-900 dark:hover:text-zinc-100'
      }`;
    }
    return `flex items-center gap-3.5 px-3.5 py-3 rounded-full text-left rtl:text-right transition-all text-[17px] ${
      match
        ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-white font-bold'
        : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900/60 hover:text-zinc-900 dark:hover:text-zinc-100 font-medium'
    }`;
  };

  const isMobileTabActive = (path) => (
    path === '/home'
      ? location.pathname === '/home'
      : path === '/explore'
      ? location.pathname === '/explore' || location.pathname === '/search' || location.pathname.startsWith('/hashtag/')
      : location.pathname.startsWith(path)
  );

  const mobileBottomLinkClass = (path) => (
    `flex-1 flex flex-col items-center justify-center py-2 transition-colors relative ${
      isMobileTabActive(path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
    }`
  );

  // Tab icon: filled when its tab is active, outlined otherwise
  const mobileTabIcon = (path, icon) => (
    <i className={`${isMobileTabActive(path) ? 'fas' : 'far'} fa-${icon} text-xl`} />
  );

  // Font Awesome Free has no outlined house, so Home is drawn here
  const mobileHomeIcon = () => (
    isMobileTabActive('/home') ? (
      <svg className="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
        <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
      </svg>
    ) : (
      <svg className="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    )
  );

  // Count badge pinned to the icon's top-right corner, clear of the create button
  const mobileTabBadge = (count, color) => (
    <span className={`absolute -top-1 -right-1.5 rtl:right-auto rtl:-left-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full ring-2 ${isLamha ? 'ring-black' : 'ring-white dark:ring-black'} text-white text-[10px] font-bold leading-none ${color}`}>
      {count > 99 ? '99+' : count}
    </span>
  );

  const profileAvatar = (size = 'w-10 h-10', textClass = 'text-sm') => {
    const pic = profileSummary?.profilePicture || user?.profilePicture || user?.profile_picture;
    if (pic) {
      return (
        <img
          src={getMediaUrl(pic)}
          alt={user?.username || 'User'}
          className={`${size} rounded-full object-cover border border-zinc-200 dark:border-zinc-800`}
        />
      );
    }
    return (
      <div className={`${size} rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 ${textClass}`}>
        {user?.username?.charAt(0).toUpperCase() || 'U'}
      </div>
    );
  };

  const menuContent = user ? (
    <>
      <Link to="/home" onClick={closeDrawer} className={navLinkClass('/home')} title={t('nav.home', 'Home')}>
        <i className="fas fa-home w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.home', 'Home')}</span>}
      </Link>
      <Link to="/explore" onClick={closeDrawer} className={navLinkClass('/explore')} title={t('nav.explore', 'Explore')}>
        <i className="fas fa-compass w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.explore', 'Explore')}</span>}
      </Link>
      <Link to="/lamha" onClick={closeDrawer} className={navLinkClass('/lamha')} title={t('nav.lamha', 'Lamha')}>
        <i className="fas fa-circle-play w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.lamha', 'Lamha')}</span>}
      </Link>
      <Link to="/notifications" onClick={closeDrawer} className={navLinkClass('/notifications') + ' relative'} title={t('nav.notifications', 'Notifications')}>
        <span className="relative flex items-center justify-center">
          <i className="fas fa-bell w-5 text-center text-lg" />
          {unreadNotificationsCount > 0 && (
            <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-rose-500 text-white text-[11px] font-bold rounded-full">
              {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
            </span>
          )}
        </span>
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.notifications', 'Notifications')}</span>}
      </Link>
      <Link to="/messages" onClick={closeDrawer} className={navLinkClass('/messages') + ' relative'} title={t('nav.messages', 'Messages')}>
        <span className="relative flex items-center justify-center">
          <i className="fas fa-envelope w-5 text-center text-lg" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-sky-500 text-white text-[11px] font-bold rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.messages', 'Messages')}</span>}
      </Link>
      <Link to="/bookmarks" onClick={closeDrawer} className={navLinkClass('/bookmarks')} title={t('nav.bookmarks', 'Bookmarks')}>
        <i className="fas fa-bookmark w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.bookmarks', 'Bookmarks')}</span>}
      </Link>
      <Link to="/communities" onClick={closeDrawer} className={navLinkClass('/communities') + ' relative'} title={t('nav.communities', 'Communities')}>
        <span className="relative flex items-center justify-center">
          <i className="fas fa-users w-5 text-center text-lg" />
          {communitiesUnreadCount > 0 && (
            <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-sky-500 text-white text-[11px] font-bold rounded-full">
              {communitiesUnreadCount > 99 ? '99+' : communitiesUnreadCount}
            </span>
          )}
        </span>
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.communities', 'Communities')}</span>}
      </Link>
      <Link to="/marketplace" onClick={closeDrawer} className={navLinkClass('/marketplace')} title={t('nav.marketplace', 'Marketplace')}>
        <i className="fas fa-store w-5 text-center text-lg text-emerald-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.marketplace', 'Marketplace')}</span>}
      </Link>
      <Link to="/impact" onClick={closeDrawer} className={navLinkClass('/impact')} title={t('nav.impact', 'Civic Impact')}>
        <i className="fas fa-hand-holding-heart w-5 text-center text-lg text-rose-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.impact', 'Civic Impact')}</span>}
      </Link>
      <Link to={`/profile/${user?.handle}`} onClick={closeDrawer} className={navLinkClass(`/profile/${user?.handle}`)} title={t('nav.profile', 'Profile')}>
        <i className="fas fa-user w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.profile', 'Profile')}</span>}
      </Link>
      <button
        type="button"
        onClick={() => {
          closeDrawer();
          triggerHaptic('light');
          setShowQrModal(true);
        }}
        className={navLinkClass('/qr') + ' w-full text-left rtl:text-right cursor-pointer'}
        title={t('nav.qrCode', 'My QR Code')}
      >
        <i className="fas fa-qrcode w-5 text-center text-lg text-indigo-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.qrCode', 'My QR Code')}</span>}
      </button>
      <Link to="/halqa" onClick={closeDrawer} className={navLinkClass('/halqa')} title={t('nav.halqa', 'Halqa Spaces')}>
        <i className="fas fa-microphone-alt w-5 text-center text-lg text-rose-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.halqa', 'Halqa Spaces')}</span>}
      </Link>
      <Link to="/settings" onClick={closeDrawer} className={navLinkClass('/settings')} title={t('nav.settings', 'Settings')}>
        <i className="fas fa-cog w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.settings', 'Settings')}</span>}
      </Link>
      {user?.isAdmin && (
        <Link to="/admin" onClick={closeDrawer} className={navLinkClass('/admin')} title="Admin Control">
          <i className="fas fa-shield-alt w-5 text-center text-lg text-amber-500" />
          {(!sidebarCollapsed || isMobile) && (
            <span className="flex items-center gap-1.5 font-bold text-amber-600 dark:text-amber-400">
              Admin
            </span>
          )}
        </Link>
      )}

      {/* Post CTA Button */}
      {sidebarCollapsed && !isMobile ? (
        <Link
          to="/compose"
          onClick={closeDrawer}
          className="w-11 h-11 mx-auto rounded-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 flex items-center justify-center shadow-md hover:scale-105 active:scale-95 transition-all mt-3 mb-2 shrink-0"
          aria-label={t('nav.createPost', 'Post')}
          title={t('nav.post', 'Post')}
        >
          <i className="fas fa-feather-alt text-base" />
        </Link>
      ) : (
        <Link
          to="/compose"
          onClick={closeDrawer}
          className="flex items-center justify-center gap-2 w-full py-3.5 px-4 rounded-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[16px] font-bold hover:bg-zinc-800 dark:hover:bg-zinc-100 shadow-md hover:shadow-lg transition-all mt-3 mb-2"
          aria-label={t('nav.createPost', 'Post')}
        >
          <i className="fas fa-feather-alt text-sm" />
          <span>{t('nav.post', 'Post')}</span>
        </Link>
      )}

      {/* Halqa — Start a Live Audio Room */}
      <HalqaStartButton collapsed={sidebarCollapsed && !isMobile} />
    </>
  ) : (
    <>
      <Link to="/explore" onClick={closeDrawer} className={navLinkClass('/explore')} title={t('nav.explore', 'Explore')}>
        <i className="fas fa-compass w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.explore', 'Explore')}</span>}
      </Link>
      <Link to="/lamha" onClick={closeDrawer} className={navLinkClass('/lamha')} title={t('nav.lamha', 'Lamha')}>
        <i className="fas fa-circle-play w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.lamha', 'Lamha')}</span>}
      </Link>
      <Link to="/search" onClick={closeDrawer} className={navLinkClass('/search')} title={t('nav.search', 'Search')}>
        <i className="fas fa-search w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.search', 'Search')}</span>}
      </Link>
      <Link to="/communities" onClick={closeDrawer} className={navLinkClass('/communities')} title={t('nav.communities', 'Communities')}>
        <i className="fas fa-users w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.communities', 'Communities')}</span>}
      </Link>
      <Link to="/marketplace" onClick={closeDrawer} className={navLinkClass('/marketplace')} title={t('nav.marketplace', 'Marketplace')}>
        <i className="fas fa-store w-5 text-center text-lg text-emerald-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.marketplace', 'Marketplace')}</span>}
      </Link>
      <Link to="/impact" onClick={closeDrawer} className={navLinkClass('/impact')} title={t('nav.impact', 'Civic Impact')}>
        <i className="fas fa-hand-holding-heart w-5 text-center text-lg text-rose-500" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.impact', 'Civic Impact')}</span>}
      </Link>
      <Link to="/login" onClick={closeDrawer} className={navLinkClass('/login')} title={t('nav.logIn', 'Log in')}>
        <i className="fas fa-sign-in-alt w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.logIn', 'Log in')}</span>}
      </Link>
      <Link to="/register" onClick={closeDrawer} className={navLinkClass('/register')} title={t('nav.signUp', 'Sign up')}>
        <i className="fas fa-user-plus w-5 text-center text-lg" />
        {(!sidebarCollapsed || isMobile) && <span>{t('nav.signUp', 'Sign up')}</span>}
      </Link>
    </>
  );

  return (
    <div className={`min-h-screen ${!isMobile ? 'h-screen overflow-hidden flex justify-center bg-white dark:bg-black' : isMessagesPage ? 'h-screen max-h-screen overflow-hidden flex flex-col bg-white dark:bg-black' : 'bg-white dark:bg-black'}`}>
      {/* Floating Interactive In-App Notification Toast (Native iOS Dynamic Push Banner) */}
      {incomingMessageToast && (
        <aside
          aria-label="Notification alert"
          className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none flex justify-center px-3 sm:px-4 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)]"
        >
          <div
            onClick={() => {
              triggerHaptic('light');
              navigate(incomingMessageToast.targetUrl || `/messages/${incomingMessageToast.senderId}`);
              setIncomingMessageToast(null);
            }}
            onTouchStart={(e) => {
              bannerTouchStartYRef.current = e.touches[0].clientY;
            }}
            onTouchMove={(e) => {
              if (bannerTouchStartYRef.current === null) return;
              const deltaY = e.touches[0].clientY - bannerTouchStartYRef.current;
              if (deltaY < 0) {
                setBannerDragY(deltaY);
              }
            }}
            onTouchEnd={() => {
              if (bannerDragY < -24) {
                dismissIncomingBanner();
              } else {
                setBannerDragY(0);
              }
              bannerTouchStartYRef.current = null;
            }}
            style={bannerDragY < 0 ? { transform: `translate3d(0, ${bannerDragY}px, 0)` } : undefined}
            className={`pointer-events-auto w-full max-w-[420px] p-3 sm:p-3.5 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-2xl border border-black/[0.08] dark:border-white/[0.12] rounded-[24px] sm:rounded-[26px] shadow-[0_16px_40px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.04)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6),0_2px_10px_rgba(0,0,0,0.3)] cursor-pointer hover:border-zinc-300 dark:hover:border-zinc-700 transition-all select-none active:scale-[0.985] group ${
              bannerIsDismissing ? 'native-banner-exit' : 'native-banner-enter'
            }`}
            role="alert"
          >
            {/* Top iOS Grab Indicator */}
            <div className="w-8 h-1 rounded-full bg-zinc-300/80 dark:bg-zinc-700/80 mx-auto -mt-1 mb-2 opacity-60" />

            {/* Notification App Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-[5px] bg-gradient-to-tr from-sky-500 via-indigo-500 to-purple-600 flex items-center justify-center text-white text-[8px] font-black shadow-2xs">
                  B
                </div>
                <span className="text-[10.5px] font-bold tracking-wider uppercase text-zinc-500 dark:text-zinc-400">
                  BAZZEF
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500 font-medium">
                  {t('common.now', 'Now')}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissIncomingBanner();
                  }}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                  aria-label="Dismiss notification"
                >
                  <i className="fas fa-times text-[10px]" />
                </button>
              </div>
            </div>

            {/* Notification Main Content */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                <img
                  src={
                    getMediaUrl(incomingMessageToast.avatar) ||
                    `https://api.dicebear.com/7.x/identicon/svg?seed=${incomingMessageToast.senderHandle || 'user'}`
                  }
                  alt=""
                  className="w-10 h-10 rounded-2xl object-cover ring-1 ring-black/10 dark:ring-white/10"
                  onError={(e) => {
                    e.currentTarget.src = `https://api.dicebear.com/7.x/identicon/svg?seed=${incomingMessageToast.senderHandle || 'user'}`;
                  }}
                />
                {(() => {
                  switch (incomingMessageToast.type) {
                    case 'like':
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-heart" />
                        </div>
                      );
                    case 'reply':
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-reply" />
                        </div>
                      );
                    case 're-bazz':
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-retweet" />
                        </div>
                      );
                    case 'follow':
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-purple-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-user-plus" />
                        </div>
                      );
                    case 'mention':
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-at" />
                        </div>
                      );
                    default:
                      return (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-sky-500 text-white flex items-center justify-center text-[7.5px] ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                          <i className="fas fa-comment text-[8px]" />
                        </div>
                      );
                  }
                })()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-zinc-900 dark:text-zinc-100 text-[13.5px] leading-tight truncate">
                    {incomingMessageToast.senderName}
                  </span>
                  {incomingMessageToast.isVerified && (
                    <i className="fas fa-check-circle text-sky-500 text-[10.5px] flex-shrink-0" />
                  )}
                  {incomingMessageToast.senderHandle && (
                    <span className="text-zinc-400 dark:text-zinc-500 text-[11.5px] font-normal truncate">
                      @{incomingMessageToast.senderHandle}
                    </span>
                  )}
                </div>
                <p className="text-[12.5px] text-zinc-600 dark:text-zinc-300 line-clamp-2 leading-snug break-words mt-0.5 font-normal">
                  {incomingMessageToast.content}
                </p>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Desktop Layout */}
      {!isMobile && (
        <div className={`w-full ${isHalqaPage || isMessagesPage ? 'max-w-full' : 'max-w-7xl'} flex h-screen bg-white dark:bg-black`}>
          {/* Left Navigation Sidebar */}
          <aside
            className={`flex-shrink-0 sticky top-0 self-start h-screen z-40 flex flex-col justify-between ltr:border-r rtl:border-l border-zinc-100 dark:border-zinc-800/80 bg-white dark:bg-black transition-all duration-200 ease-in-out ${
              sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-3'
            }`}
            style={{ width: sidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_WIDTH }}
          >
            <div>
              {/* Brand Logo & Collapse Toggle */}
              {sidebarCollapsed ? (
                <div className="flex flex-col items-center gap-2 py-1 mb-3">
                  <Link to={user ? '/home' : '/explore'} className="inline-flex items-center justify-center group" title="Bazzef">
                    <img
                      src="/logo.png"
                      alt="Bazzef"
                      className="w-9 h-9 rounded-xl object-contain shadow-xs group-hover:scale-105 transition-transform"
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                    title={t('nav.expandSidebar', 'Expand sidebar')}
                    aria-label="Expand sidebar"
                  >
                    <i className={`fas ${isRtl ? 'fa-chevron-left' : 'fa-chevron-right'} text-xs`} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between px-2 py-2 mb-2">
                  <Link to={user ? '/home' : '/explore'} className="inline-flex items-center gap-2.5 group min-w-0">
                    <img
                      src="/logo.png"
                      alt="Bazzef"
                      className="w-9 h-9 rounded-xl object-contain shadow-xs group-hover:scale-105 transition-transform flex-shrink-0"
                    />
                    <span className="font-extrabold text-2xl tracking-tight text-zinc-900 dark:text-white truncate">
                      {i18n.language?.startsWith('ar') ? 'بزاف' : 'bazzef'}
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                    title={t('nav.collapseSidebar', 'Collapse sidebar')}
                    aria-label="Collapse sidebar"
                  >
                    <i className={`fas ${isRtl ? 'fa-chevron-right' : 'fa-chevron-left'} text-xs`} />
                  </button>
                </div>
              )}

              {/* Navigation Links */}
              <nav className="flex flex-col gap-1 overflow-y-auto">{menuContent}</nav>
            </div>

            {/* User Account Bar at Bottom */}
            {user && (
              <div className="pt-2 border-t border-zinc-100 dark:border-zinc-800/80">
                {sidebarCollapsed ? (
                  <div className="flex flex-col items-center gap-2 py-1">
                    <Link
                      to={`/profile/${user?.handle}`}
                      className="flex items-center justify-center rounded-full hover:ring-2 hover:ring-zinc-300 dark:hover:ring-zinc-700 transition-all"
                      title={`${profileSummary?.username || user?.username} (@${profileSummary?.handle || user?.handle})`}
                    >
                      {profileAvatar('w-9 h-9', 'text-xs')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors rounded-xl"
                      title={t('nav.logOut', 'Log out')}
                      aria-label="Log out"
                    >
                      <i className="fas fa-sign-out-alt text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-2 rounded-2xl hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">
                    <Link
                      to={`/profile/${user?.handle}`}
                      className="flex items-center gap-2.5 min-w-0 flex-1"
                    >
                      {profileAvatar('w-9 h-9', 'text-xs')}
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm text-zinc-900 dark:text-zinc-100 truncate">
                          {profileSummary?.username || user?.username}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                          @{profileSummary?.handle || user?.handle}
                        </p>
                      </div>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="text-zinc-400 hover:text-rose-500 dark:hover:text-rose-400 p-2 transition-colors rounded-lg"
                      title={t('nav.logOut', 'Log out')}
                    >
                      <i className="fas fa-sign-out-alt text-base" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* Main Feed Content Area */}
          <main className={`flex-1 flex flex-col min-h-0 min-w-0 ${isMessagesPage ? 'h-full overflow-hidden' : isHalqaPage ? 'h-full overflow-y-auto' : isLamha ? 'h-full overflow-hidden ltr:border-r rtl:border-l border-zinc-100 dark:border-zinc-800/80' : 'overflow-y-auto ltr:border-r rtl:border-l border-zinc-100 dark:border-zinc-800/80'}`}>
            <div className={`flex flex-col w-full min-h-0 bg-white dark:bg-black ${isMessagesPage ? 'h-full' : isHalqaPage ? 'w-full min-h-full' : isLamha ? 'h-full max-w-2xl mx-auto px-4 sm:px-6' : 'max-w-2xl mx-auto px-4 sm:px-6'}`}>
              {/* Live Halqa banner — shown above the feed when rooms are active */}
              {!isMessagesPage && !isHalqaPage && !isLamha && <HalqaBanner />}
              {/* Pages load on demand; keep the header/nav up and show a skeleton meanwhile */}
            <Suspense fallback={<PostListSkeleton count={3} />}>
              <Outlet context={{ hideMobileNav, setHideMobileNav, setDrawerOpen }} />
            </Suspense>
            </div>
          </main>

          {/* Right Sidebar (Trends, Follows, Search) */}
          {!isMessagesPage && !isHalqaPage && <RightSidebar />}
        </div>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <>
          {/* Mobile Top Header (Messages provides its own unified top header, and chat thread is full screen) */}
          {!isMessagesPage && !isHalqaRoom && !isCompose && !isLamha && (
            <>
              {(() => {
                const headerConfig = getHeaderConfig();
                const isMessagesListPage = location.pathname.startsWith('/messages');
                return (
                  <header className={`fixed top-0 left-0 right-0 h-[calc(3rem+env(safe-area-inset-top))] flex items-center justify-between px-3 sm:px-4 pt-[env(safe-area-inset-top)] ${isMessagesListPage ? 'border-b-0' : 'border-b border-zinc-100 dark:border-zinc-800/80'} bg-white/95 dark:bg-black/95 backdrop-blur-md z-30 transition-all`}>
                    {!mobileSearchOpen ? (
                      <>
                        <div className="flex items-center gap-2 min-w-[36px]">
                          {headerConfig.left === 'back' ? (
                            <button
                              type="button"
                              onClick={() => {
                                triggerHaptic('light');
                                if (window.history.state && window.history.state.idx > 0) {
                                  navigate(-1);
                                } else {
                                  navigate(user ? '/home' : '/explore');
                                }
                              }}
                              className="w-11 h-11 -ml-2.5 rtl:ml-0 rtl:-mr-2.5 rounded-full flex items-center justify-center text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-white active:scale-90 active:bg-zinc-100 dark:active:bg-zinc-800 transition-all touch-press"
                              aria-label={t('common.back', 'Back')}
                            >
                              <i className="fas fa-arrow-left text-base" />
                            </button>
                          ) : user ? (
                            <button
                              type="button"
                              onClick={() => setDrawerOpen(true)}
                              className="focus:outline-none touch-press active:scale-90"
                              aria-label="Open Drawer"
                            >
                              {profileAvatar('w-8 h-8', 'text-xs')}
                            </button>
                          ) : (
                            <Link to="/login" className="text-sm font-semibold text-sky-600 dark:text-sky-400">
                              Log in
                            </Link>
                          )}
                        </div>

                        {headerConfig.center === 'brand' ? (
                          <Link to={user ? '/home' : '/explore'} className="flex items-center gap-1.5 active:scale-95 transition-transform touch-press">
                            <img
                              src="/logo.png"
                              alt="Bazzef"
                              className="w-7 h-7 rounded-lg object-contain shadow-xs"
                            />
                            <span className="font-extrabold text-lg tracking-tight text-zinc-900 dark:text-white">
                              {i18n.language?.startsWith('ar') ? 'بزاف' : 'bazzef'}
                            </span>
                          </Link>
                        ) : (
                          <h1 className="font-bold text-[17px] text-zinc-900 dark:text-zinc-100 truncate max-w-[210px] text-center tracking-tight">
                            {headerConfig.title}
                          </h1>
                        )}

                        <div className="flex items-center justify-end min-w-[36px]">
                          {headerConfig.right === 'search' ? (
                            <button
                              type="button"
                              onClick={() => {
                                triggerHaptic('light');
                                setMobileSearchOpen(true);
                              }}
                              className="w-11 h-11 -mr-2.5 rtl:mr-0 rtl:-ml-2.5 rounded-full flex items-center justify-center text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-90 transition-all touch-press"
                              aria-label={t('common.search', 'Search')}
                              title={t('common.search', 'Search')}
                            >
                              <i className="fas fa-search text-base" />
                            </button>
                          ) : headerConfig.right === 'compose' ? (
                            <button
                              type="button"
                              onClick={() => {
                                triggerHaptic('light');
                                window.dispatchEvent(new CustomEvent('open-new-message'));
                              }}
                              className="w-11 h-11 -mr-2.5 rtl:mr-0 rtl:-ml-2.5 rounded-full flex items-center justify-center text-zinc-700 dark:text-zinc-300 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-90 transition-all touch-press"
                              aria-label={t('messages.newMessage', 'New message')}
                              title={t('messages.newMessage', 'New message')}
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                              </svg>
                            </button>
                          ) : headerConfig.right === 'createCommunity' ? (
                            <Link
                              to="/communities/create"
                              onClick={() => triggerHaptic('light')}
                              className="w-11 h-11 -mr-2.5 rtl:mr-0 rtl:-ml-2.5 rounded-full flex items-center justify-center text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-90 transition-all touch-press"
                              aria-label={t('communities.create', 'Create Community')}
                              title={t('communities.create', 'Create Community')}
                            >
                              <i className="fas fa-plus text-lg" />
                            </Link>
                          ) : (
                            <div className="w-9 h-9" aria-hidden="true" />
                          )}
                        </div>
                      </>
                    ) : (
                      /* Morphing Search Header */
                      <div className="flex items-center gap-2 w-full animate-in fade-in duration-150">
                        <button
                          type="button"
                          onClick={() => {
                            triggerHaptic('light');
                            setMobileSearchOpen(false);
                          }}
                          className="w-11 h-11 -ml-2.5 rtl:ml-0 rtl:-mr-2.5 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 shrink-0 transition-colors touch-press active:scale-90"
                          aria-label={t('common.back', 'Back')}
                        >
                          <i className="fas fa-arrow-left text-sm" />
                        </button>

                        <form onSubmit={handleMobileSearchSubmit} className="flex-1 relative flex items-center min-w-0">
                          <i className="fas fa-search absolute ltr:left-3 rtl:right-3 text-xs text-zinc-400 pointer-events-none" />
                          <input
                            ref={mobileSearchInputRef}
                            type="search"
                            value={mobileSearchQuery}
                            onChange={(e) => setMobileSearchQuery(e.target.value)}
                            placeholder={t('nav.searchPlaceholder', 'Search users, posts, topics...')}
                            className="w-full ltr:pl-8 ltr:pr-7 rtl:pr-8 rtl:pl-7 py-1.5 text-sm bg-zinc-100 dark:bg-zinc-800/90 rounded-full border border-transparent focus:border-sky-500 focus:bg-white dark:focus:bg-zinc-900 focus:outline-none transition-all text-zinc-900 dark:text-white placeholder-zinc-400"
                            autoCapitalize="none"
                            autoCorrect="off"
                          />
                          {mobileSearchQuery && (
                            <button
                              type="button"
                              onClick={() => setMobileSearchQuery('')}
                              className="absolute ltr:right-2.5 rtl:left-2.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-0.5"
                              aria-label="Clear"
                            >
                              <i className="fas fa-times-circle text-xs" />
                            </button>
                          )}
                        </form>

                        <button
                          type="button"
                          onClick={handleMobileSearchSubmit}
                          disabled={!mobileSearchQuery.trim()}
                          className="px-3.5 py-1.5 bg-sky-500 hover:bg-sky-600 text-white rounded-full text-xs font-semibold active:scale-95 disabled:opacity-40 transition-all shrink-0 shadow-xs touch-press"
                        >
                          {t('common.search', 'Search')}
                        </button>
                      </div>
                    )}
                  </header>
                );
              })()}

              {/* Mobile Search Overlay with Quick Explore shortcuts */}
              {mobileSearchOpen && (
                <>
                  <div
                    className="fixed inset-0 top-[calc(3rem+env(safe-area-inset-top))] bg-black/60 backdrop-blur-xs z-20 md:hidden animate-in fade-in duration-150"
                    onClick={() => setMobileSearchOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="fixed top-[calc(3rem+env(safe-area-inset-top))] left-0 right-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border-b border-zinc-200/80 dark:border-zinc-800 p-3.5 z-30 shadow-xl md:hidden animate-in slide-in-from-top-2 duration-150">
                    <p className="text-[11px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-2">
                      {t('search.quickExplore', 'Quick Explore')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          triggerHaptic('light');
                          navigate('/explore');
                          setMobileSearchOpen(false);
                        }}
                        className="px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 hover:text-sky-500 transition-colors flex items-center gap-1.5"
                      >
                        <i className="fas fa-fire text-amber-500 text-xs" />
                        <span>{t('explore.trending', 'Trending')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          triggerHaptic('light');
                          navigate('/halqa');
                          setMobileSearchOpen(false);
                        }}
                        className="px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-500 transition-colors flex items-center gap-1.5"
                      >
                        <i className="fas fa-microphone-alt text-rose-500 text-xs" />
                        <span>{t('nav.halqa', 'Halqa Spaces')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          triggerHaptic('light');
                          navigate('/communities');
                          setMobileSearchOpen(false);
                        }}
                        className="px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 hover:text-sky-500 transition-colors flex items-center gap-1.5"
                      >
                        <i className="fas fa-users text-sky-500 text-xs" />
                        <span>{t('nav.communities', 'Communities')}</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Mobile Drawer (Settings, Bookmarks, Communities, Profile) */}
          {drawerOpen && (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 md:hidden"
              onClick={closeDrawer}
              aria-hidden="true"
            />
          )}

          <div
            className={`fixed top-0 start-0 bottom-0 z-50 w-[min(300px,85vw)] bg-white dark:bg-zinc-950 shadow-2xl flex flex-col transition-transform duration-200 ease-out md:hidden pt-[env(safe-area-inset-top)] ${
              drawerOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
            }`}
          >
            <div className="p-4 border-b border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {profileAvatar('w-11 h-11', 'text-base')}
                <div className="min-w-0">
                  <p className="font-bold text-zinc-900 dark:text-zinc-100 truncate text-[16px]">
                    {profileSummary?.username || user?.username}
                  </p>
                  <p className="text-zinc-500 dark:text-zinc-400 text-xs truncate">
                    @{profileSummary?.handle || user?.handle}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              >
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            <div className="p-3 flex gap-4 border-b border-zinc-100 dark:border-zinc-800/80 text-sm">
              <Link to={user?.handle ? `/profile/${user.handle}/following` : '/follow/following'} onClick={closeDrawer}>
                <span className="font-bold text-zinc-900 dark:text-white">
                  {profileSummary?.followingCount ?? 0}
                </span>{' '}
                <span className="text-zinc-500">{t('profile.following', 'Following')}</span>
              </Link>
              <Link to={user?.handle ? `/profile/${user.handle}/followers` : '/follow/followers'} onClick={closeDrawer}>
                <span className="font-bold text-zinc-900 dark:text-white">
                  {profileSummary?.followersCount ?? 0}
                </span>{' '}
                <span className="text-zinc-500">{t('profile.followers', 'Followers')}</span>
              </Link>
            </div>

            <nav className="p-3 flex flex-col gap-1 overflow-y-auto flex-1">
              <Link to="/explore" onClick={closeDrawer} className={navLinkClass('/explore')}>
                <i className="fas fa-compass w-5 text-center text-lg" />
                <span>{t('nav.explore', 'Explore')}</span>
              </Link>
              <Link to="/lamha" onClick={closeDrawer} className={navLinkClass('/lamha')}>
                <i className="fas fa-circle-play w-5 text-center text-lg" />
                <span>{t('nav.lamha', 'Lamha')}</span>
              </Link>
              <Link to={`/profile/${user?.handle}`} onClick={closeDrawer} className={navLinkClass(`/profile/${user?.handle}`)}>
                <i className="fas fa-user w-5 text-center text-lg" />
                <span>{t('nav.profile', 'Profile')}</span>
              </Link>
              <Link to="/bookmarks" onClick={closeDrawer} className={navLinkClass('/bookmarks')}>
                <i className="fas fa-bookmark w-5 text-center text-lg" />
                <span>{t('nav.bookmarks', 'Bookmarks')}</span>
              </Link>
              <Link to="/communities" onClick={closeDrawer} className={navLinkClass('/communities')}>
                <i className="fas fa-users w-5 text-center text-lg" />
                <span>{t('nav.communities', 'Communities')}</span>
              </Link>
              <Link to="/marketplace" onClick={closeDrawer} className={navLinkClass('/marketplace')}>
                <i className="fas fa-store w-5 text-center text-lg text-emerald-500" />
                <span>{t('nav.marketplace', 'Marketplace')}</span>
              </Link>
              <Link to="/impact" onClick={closeDrawer} className={navLinkClass('/impact')}>
                <i className="fas fa-hand-holding-heart w-5 text-center text-lg text-rose-500" />
                <span>{t('nav.impact', 'Civic Impact')}</span>
              </Link>
              <button
                type="button"
                onClick={() => {
                  closeDrawer();
                  triggerHaptic('light');
                  setShowQrModal(true);
                }}
                className={navLinkClass('/qr') + ' w-full text-left rtl:text-right cursor-pointer'}
              >
                <i className="fas fa-qrcode w-5 text-center text-lg text-indigo-500" />
                <span>{t('nav.qrCode', 'My QR Code')}</span>
              </button>
              <Link to="/halqa" onClick={closeDrawer} className={navLinkClass('/halqa')}>
                <i className="fas fa-microphone-alt w-5 text-center text-lg text-rose-500" />
                <span>{t('nav.halqa', 'Halqa Spaces')}</span>
              </Link>
              {user?.isAdmin && (
                <Link to="/admin" onClick={closeDrawer} className={navLinkClass('/admin')}>
                  <i className="fas fa-shield-alt w-5 text-center text-lg text-amber-500" />
                  <span className="font-bold text-amber-600 dark:text-amber-400">Admin</span>
                </Link>
              )}
            </nav>

            <SiteLinks className="px-5 py-3" onNavigate={closeDrawer} />

            {/* Pinned Drawer Bottom: Settings (Gear) & Logout */}
            <div className="p-3 border-t border-zinc-100 dark:border-zinc-800/80 flex flex-col gap-1 bg-zinc-50/50 dark:bg-zinc-900/40 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
              <Link to="/settings" onClick={closeDrawer} className={navLinkClass('/settings')}>
                <i className="fas fa-cog w-5 text-center text-lg text-zinc-500 dark:text-zinc-400" />
                <span>{t('nav.settings', 'Settings')}</span>
              </Link>
              {user && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-full text-left transition-colors text-[16px] text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                >
                  <i className="fas fa-sign-out-alt w-5 text-center text-lg" />
                  <span>{t('nav.logOut', 'Log out')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <main
            className={`flex-1 flex flex-col min-h-0 min-w-0 ${
              isMessagesPage
                ? (shouldHideMobileNav
                    ? 'h-full overflow-hidden'
                    : 'h-[calc(100vh-3.5rem)] max-h-screen overflow-hidden')
                : isHalqaRoom
                ? 'h-[calc(100dvh-3.5rem)] min-h-0 pt-0 pb-0 overflow-y-auto'
                : isCompose
                ? 'min-h-screen pb-[env(safe-area-inset-bottom,0px)]'
                : isLamhaStudio
                ? 'h-[100dvh] overflow-hidden'
                : isLamha
                ? 'h-[100dvh] overflow-hidden pb-[calc(3.5rem+env(safe-area-inset-bottom))]'
                : shouldHideMobileNav
                ? 'min-h-screen pt-[calc(3rem+env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom,0px)]'
                : 'min-h-screen pt-[calc(3rem+env(safe-area-inset-top))] pb-[calc(4rem+env(safe-area-inset-bottom))]'
            }`}
          >
            {/* Live Halqa banner — shown above the feed when rooms are active */}
            {!isMessagesPage && !isHalqaPage && !isLamha && <HalqaBanner />}
            {/* Pages load on demand; keep the header/nav up and show a skeleton meanwhile */}
            <Suspense fallback={<PostListSkeleton count={3} />}>
              <div
                key={isMessagesPage ? 'messages-root' : isLamha ? 'lamha-root' : location.pathname}
                className="flex-1 flex flex-col min-h-0 min-w-0 screen-transition-enter"
              >
                <Outlet context={{ hideMobileNav: shouldHideMobileNav, setHideMobileNav, setDrawerOpen }} />
              </div>
            </Suspense>
          </main>

          {/* Mobile Bottom Navigation Bar (First-Class Social Ergonomics) */}
          {!shouldHideMobileNav && (
            <nav className={`fixed bottom-0 left-0 right-0 h-[calc(3.5rem+env(safe-area-inset-bottom))] backdrop-blur-md border-t z-40 flex items-center justify-around px-2 pb-[env(safe-area-inset-bottom)] select-none ${
              isLamha ? 'bg-black border-zinc-800/80' : 'bg-white/95 dark:bg-black/95 border-zinc-100 dark:border-zinc-800/80'
            }`}>
            {/* Logged-out visitors have no home feed, so their first tab is Explore */}
            {user ? (
              <Link
                to="/home"
                onClick={() => triggerHaptic('light')}
                className={`${mobileBottomLinkClass('/home')} touch-press`}
                aria-label={t('nav.home', 'Home')}
              >
                {mobileHomeIcon()}
              </Link>
            ) : (
              <Link
                to="/explore"
                onClick={() => triggerHaptic('light')}
                className={`${mobileBottomLinkClass('/explore')} touch-press`}
                aria-label={t('nav.explore', 'Explore')}
              >
                {mobileTabIcon('/explore', 'compass')}
              </Link>
            )}
            <Link
              to="/lamha"
              onClick={() => triggerHaptic('light')}
              className={`${mobileBottomLinkClass('/lamha')} touch-press`}
              aria-label={t('nav.lamha', 'Lamha')}
            >
              {mobileTabIcon('/lamha', 'circle-play')}
            </Link>
            <button
              type="button"
              onClick={() => {
                triggerHaptic('medium');
                setCreateMenuOpen((open) => !open);
              }}
              className={`flex items-center justify-center w-10 h-10 rounded-full shadow-md active:scale-85 transition-transform touch-press ${
                isLamha ? 'bg-white text-zinc-900' : 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
              }`}
              aria-label={t('nav.create', 'Create')}
              aria-haspopup="menu"
              aria-expanded={createMenuOpen}
            >
              <i className={`fas fa-plus text-base transition-transform duration-150 ${createMenuOpen ? 'rotate-45' : ''}`} />
            </button>
            <Link
              to="/notifications"
              onClick={() => triggerHaptic('light')}
              className={`${mobileBottomLinkClass('/notifications')} touch-press`}
              aria-label="Notifications"
            >
              <span className="relative inline-block">
                {mobileTabIcon('/notifications', 'bell')}
                {unreadNotificationsCount > 0 && mobileTabBadge(unreadNotificationsCount, 'bg-rose-500')}
              </span>
            </Link>
            <Link
              to="/messages"
              onClick={() => triggerHaptic('light')}
              className={`${mobileBottomLinkClass('/messages')} touch-press`}
              aria-label="Messages"
            >
              <span className="relative inline-block">
                {mobileTabIcon('/messages', 'envelope')}
                {unreadCount > 0 && mobileTabBadge(unreadCount, 'bg-sky-500')}
              </span>
            </Link>
          </nav>
          )}

          {/* Arrow that follows a swipe-back gesture (positioned by useSwipeBack) */}
          <div
            ref={swipeIndicatorRef}
            aria-hidden="true"
            style={{ opacity: 0 }}
            className="fixed z-50 w-10 h-10 rounded-full flex items-center justify-center pointer-events-none shadow-lg bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-100 data-[armed=true]:bg-zinc-900 data-[armed=true]:text-white dark:data-[armed=true]:bg-white dark:data-[armed=true]:text-zinc-900"
          >
            <i className="fas fa-arrow-left text-sm" />
          </div>

          {/* "+" menu: what to create */}
          {createMenuOpen && !shouldHideMobileNav && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/30"
                onClick={() => setCreateMenuOpen(false)}
                aria-hidden="true"
              />
              <div
                role="menu"
                aria-label={t('nav.create', 'Create')}
                className="fixed inset-x-0 mx-auto bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-50 w-52 p-1.5 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-150"
              >
                <Link
                  to="/compose"
                  role="menuitem"
                  onClick={() => triggerHaptic('light')}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:bg-zinc-100 dark:active:bg-zinc-800"
                >
                  <i className="fas fa-feather-alt w-5 text-center text-zinc-500 dark:text-zinc-400" />
                  <span>{t('nav.post', 'Post')}</span>
                </Link>
                <Link
                  to="/lamha/create"
                  role="menuitem"
                  onClick={() => triggerHaptic('light')}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:bg-zinc-100 dark:active:bg-zinc-800"
                >
                  <i className="fas fa-circle-play w-5 text-center text-zinc-500 dark:text-zinc-400" />
                  <span>{t('nav.lamha', 'Lamha')}</span>
                </Link>
              </div>
            </>
          )}
        </>
      )}
      {showQrModal && user && (
        <BazzefQrModal
          isOpen={showQrModal}
          onClose={() => setShowQrModal(false)}
          user={user}
          initialTab="code"
        />
      )}
    </div>
  );
};

export default AppLayout;
